import { Terminal } from "lucide-react";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { CommunityLink, defaultLinks, getIconComponent } from "@/data/communityLinks";

const Footer = () => {
  const [communityLinks, setCommunityLinks] = useState<CommunityLink[]>(defaultLinks.filter((l) => l.icon !== "whatsapp"));

  useEffect(() => {
    const fetchLinks = async () => {
      const { data } = await supabase
        .from("community_links")
        .select("*")
        .eq("is_active", true)
        .order("sort_order");
      const rows = (data as CommunityLink[] | null) ?? [];
      const social = rows.filter((l) => (l.category ?? "social") === "social" && l.icon !== "whatsapp");
      if (social.length > 0) setCommunityLinks(social);
    };
    fetchLinks();
  }, []);

  const quickLinks = [
    { label: "Founder", path: "/founder" },
    { label: "Core Team", path: "/core" },
    { label: "Partners", path: "/partners" },
    { label: "Sponsorship", path: "/sponsorship" },
    { label: "Start Division", path: "/start-division" },
    { label: "Partner with Us", path: "/partner-with-us" },
    { label: "Careers", path: "/careers" },
  ];

  const communityPageLinks = [
    { label: "History", path: "/history" },
    { label: "Rules and Regulations", path: "/rules" },
    { label: "Terms & Conditions", path: "/terms" },
    { label: "Legal", path: "/legal" },
  ];

  return (
    <footer className="relative px-6 py-12 border-t border-border/50 backdrop-blur-sm bg-card/30">
      <div className="container mx-auto max-w-6xl relative z-10">
        <div className="grid md:grid-cols-3 gap-8 mb-8">
          {/* Brand */}
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Terminal className="w-6 h-6 text-primary" />
              <span className="font-terminal font-semibold text-lg">WhiteHat Hub</span>
            </div>
            <p className="text-xs text-muted-foreground font-terminal mb-4">Since 2025</p>
            <p className="text-sm text-muted-foreground font-terminal mb-1">Cybersecurity Community</p>
            <p className="text-xs text-muted-foreground font-terminal mb-4">
              We started our community on <time dateTime="2025-06-23">23 June 2025</time>
            </p>

            <ul className="flex flex-wrap gap-1 list-none p-0 m-0">
              {communityLinks.map((link) => (
                <li key={link.id}>
                  <a
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer me"
                    aria-label={link.name}
                    title={link.name}
                    className="inline-flex items-center justify-center w-11 h-11 rounded-full bg-muted/60 text-muted-foreground hover:bg-primary hover:text-primary-foreground hover:-translate-y-1 transition-all duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  >
                    <span className="w-4 h-4" aria-hidden="true">{getIconComponent(link.icon, link.icon_url)}</span>
                  </a>
                </li>
              ))}
            </ul>

          </div>

          {/* Quick Links */}
          <div>
            <h2 className="font-semibold mb-4 text-foreground text-base">Quick Links</h2>
            <ul className="space-y-2">
              {quickLinks.map((link) => (
                <li key={link.path}>
                  <Link to={link.path} className="text-sm text-muted-foreground hover:text-foreground transition-colors">
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Community */}
          <div>
            <h2 className="font-semibold mb-4 text-foreground text-base">Community</h2>
            <ul className="space-y-2">
              {communityPageLinks.map((link) => (
                <li key={link.path}>
                  <Link to={link.path} className="text-sm text-muted-foreground hover:text-foreground transition-colors">
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="pt-8 border-t border-border/50" />
      </div>
    </footer>
  );
};

export default Footer;
